/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { MaterialReactTable } from "material-react-table";
import { Box, IconButton, TextField, InputAdornment } from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import AllergiesHeader from "./Header/AllergiesHeader";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import SaveAllergyPopup from "./Popup/SaveAllergyPopup";

const AllergiesListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");

  const [isLoading, setIsLoading] = useState(false);
  const [allergyList, setAllergyList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedAllergyIds, setSelectedAllergyIds] = useState([]);
  const [selectedAllergyId, setSelectedAllergyId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Inline editing state
  const [editingCell, setEditingCell] = useState({ rowId: null, column: null });
  const [editingValue, setEditingValue] = useState("");

  // Get all allergies
  const getAllAllergies = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_ALLERGY;
      const response = await getData(requestUrl);
      setIsLoading(false);
      if (response.status) {
        setAllergyList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Bulk update allergies
  const updateBulkAllergyHandler = async () => {
    if (selectedAllergyIds.length > 0) {
      try {
        const allergyListToBeUpdate = allergyList
          .filter((item) => selectedAllergyIds.includes(item._id))
          .map((item) => ({
            _id: item._id,
            allergen: item.allergen,
            allergytype: item.allergytype,
            reportedon: item.reportedon,
            resolvedon: item.resolvedon,
            severity: item.severity,
            reaction: item.reaction,
            currentstatus: item.currentstatus,
          }));
        let allergyData = { allergylist: allergyListToBeUpdate };
        let requestURL =
          url.API_BASE_URL + url.API_UPDATE_BULK_ALLERGY;
        const response = await putData(requestURL, allergyData);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setSelectedAllergyIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one allergy");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // Bulk delete allergies
  const deleteBulkAllergyHandler = async () => {
    if (selectedAllergyIds.length > 0) {
      try {
        const allergyListToBeDelete = allergyList.filter((item) =>
          selectedAllergyIds.includes(item._id)
        );
        let allergyData = { allergylist: allergyListToBeDelete };
        let requestURL =
          url.API_BASE_URL + url.API_DELETE_BULK_ALLERGY;
        const response = await putData(requestURL, allergyData);
        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setAllergyList((prevList) =>
              prevList.filter((item) => !selectedAllergyIds.includes(item._id))
            );
            setSelectedAllergyIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }
        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one allergy");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // After any action
  const afterActionClose = (data = []) => {
    setAllergyList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllAllergies();
  }, []);

  // Save cell edits
  const handleSaveCellEdit = (rowId, column, value, extra = {}) => {
    setAllergyList((prevList) =>
      prevList.map((item) => {
        if (item._id !== rowId) return item;
        if (column === "allergen") {
          return { ...item, allergen: value?.name ?? value?.label };
        }
        if (column === "allergytype") {
          return { ...item, allergytype: value?.name ?? value?.label };
        }
        if (column === "reportedon") {
          return {
            ...item,
            reportedon: extra.isoDate || "",
            reportedondate: extra.formattedDate || "",
          };
        }
        if (column === "resolvedon") {
          return {
            ...item,
            resolvedon: extra.isoDate || "",
            resolvedondate: extra.formattedDate || "",
          };
        }
        if (column === "severity") {
          return { ...item, severity: value?.name ?? value?.label };
        }
        if (column === "reaction") {
          return { ...item, reaction: value?.name ?? value?.label };
        }
        if (column === "currentstatus") {
          return {
            ...item,
            currentstatus:
              typeof value === "string"
                ? value
                : value?.value || value?.label || "",
          };
        }
        return { ...item, [column]: value };
      })
    );
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
    setRowSelection((prevSelection) => ({
      ...prevSelection,
      [rowId]: true,
    }));
    setSelectedAllergyIds((prevIds) =>
      prevIds.includes(rowId) ? prevIds : [...prevIds, rowId]
    );
  };

  const handleCancelEdit = () => {
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
  };

  // Async load options
  const loadAllergenOptions = async (inputValue) => {
    if (!inputValue || inputValue.length <= 1) return [];
    try {
      let requestUrl = url.SERVER_URL + url.ALLERGEN_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((item) => ({
          value: item.name ?? "",
          name: item.name ?? "",
          label: item.name ?? "",
        }));
    } catch {
      return [];
    }
  };

  const loadAllergyTypeOptions = async (inputValue) => {
    if (!inputValue || inputValue.length <= 1) return [];
    try {
      let requestUrl = url.SERVER_URL + url.ALLERGY_TYPE_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((item) => ({
          value: item.name ?? "",
          name: item.name ?? "",
          label: item.name ?? "",
        }));
    } catch {
      return [];
    }
  };

  const loadSeverityOptions = async (inputValue) => {
    if (!inputValue || inputValue.length <= 1) return [];
    try {
      let requestUrl = url.SERVER_URL + url.ALLERGY_SEVERITIES_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((item) => ({
          value: item.name ?? "",
          name: item.name ?? "",
          label: item.name ?? "",
        }));
    } catch {
      return [];
    }
  };

  const loadReactionOptions = async (inputValue) => {
    if (!inputValue || inputValue.length <= 1) return [];
    try {
      let requestUrl = url.SERVER_URL + url.ALLERGY_REACTION_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((item) => ({
          value: item.name ?? "",
          name: item.name ?? "",
          label: item.name ?? "",
        }));
    } catch {
      return [];
    }
  };

  const loadConditionStatusOptions = async (inputValue) => {
    try {
      let requestUrl = url.SERVER_URL + url.CONDITION_STATUS_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes((inputValue || "").toLowerCase())
        )
        .map((item) => ({
          value: item.value ?? item.name ?? "",
          name: item.name ?? "",
          label: item.label ?? item.name ?? "",
        }));
    } catch {
      return [];
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveAllergyOffCanvas"
            aria-controls="saveAllergyOffCanvas"
            onClick={() => {
              setSelectedAllergyId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        header: "Allergen",
        accessorKey: "allergen",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "allergen" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 220, maxWidth: 320 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={false}
                  defaultOptions={false}
                  loadOptions={loadAllergenOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.allergen
                      ? {
                          value: row.original.allergen,
                          name: row.original.allergen,
                          label: row.original.allergen,
                        }
                      : null
                  }
                  onChange={setEditingValue}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                  }}
                  placeholder="Search for Allergen"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(row.original._id, "allergen", editingValue)
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "allergen",
                });
                setEditingValue(
                  row.original.allergen
                    ? {
                        value: row.original.allergen,
                        name: row.original.allergen,
                        label: row.original.allergen,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.allergen ? (
                row.original.allergen
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Allergy Type",
        accessorKey: "allergytype",
        size: 180,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "allergytype" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 180, maxWidth: 220 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={false}
                  defaultOptions={false}
                  loadOptions={loadAllergyTypeOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.allergytype
                      ? {
                          value: row.original.allergytype,
                          name: row.original.allergytype,
                          label: row.original.allergytype,
                        }
                      : null
                  }
                  onChange={setEditingValue}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                  }}
                  placeholder="Search for Allergy Type"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(
                    row.original._id,
                    "allergytype",
                    editingValue
                  )
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "allergytype",
                });
                setEditingValue(
                  row.original.allergytype
                    ? {
                        value: row.original.allergytype,
                        name: row.original.allergytype,
                        label: row.original.allergytype,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.allergytype ? (
                row.original.allergytype
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Reported Date",
        accessorKey: "reportedondate",
        size: 160,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "reportedon" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.reportedon
                  ? new Date(row.original.reportedon).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const isoDate = editingValue
                          ? new Date(editingValue).toISOString()
                          : "";
                        const formattedDate = editingValue
                          ? getDateFormatInSpanish(editingValue)
                          : "";
                        handleSaveCellEdit(
                          row.original._id,
                          "reportedon",
                          editingValue,
                          { isoDate, formattedDate }
                        );
                      }}
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isoDate = editingValue
                    ? new Date(editingValue).toISOString()
                    : "";
                  const formattedDate = editingValue
                    ? getDateFormatInSpanish(editingValue)
                    : "";
                  handleSaveCellEdit(
                    row.original._id,
                    "reportedon",
                    editingValue,
                    { isoDate, formattedDate }
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "reportedon",
                });
                setEditingValue(
                  row.original.reportedon
                    ? new Date(row.original.reportedon)
                        .toISOString()
                        .slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">{row.original.reportedondate}</span>
              </p>
            </div>
          ),
      },
      {
        header: "Resolved Date",
        accessorKey: "resolvedondate",
        size: 160,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "resolvedon" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.resolvedon
                  ? new Date(row.original.resolvedon).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const isoDate = editingValue
                          ? new Date(editingValue).toISOString()
                          : "";
                        const formattedDate = editingValue
                          ? getDateFormatInSpanish(editingValue)
                          : "";
                        handleSaveCellEdit(
                          row.original._id,
                          "resolvedon",
                          editingValue,
                          { isoDate, formattedDate }
                        );
                      }}
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isoDate = editingValue
                    ? new Date(editingValue).toISOString()
                    : "";
                  const formattedDate = editingValue
                    ? getDateFormatInSpanish(editingValue)
                    : "";
                  handleSaveCellEdit(
                    row.original._id,
                    "resolvedon",
                    editingValue,
                    { isoDate, formattedDate }
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "resolvedon",
                });
                setEditingValue(
                  row.original.resolvedon
                    ? new Date(row.original.resolvedon)
                        .toISOString()
                        .slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">{row.original.resolvedondate}</span>
              </p>
            </div>
          ),
      },
      {
        header: "Severity",
        accessorKey: "severity",
        size: 120,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "severity" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 120, maxWidth: 180 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={false}
                  defaultOptions={false}
                  loadOptions={loadSeverityOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.severity
                      ? {
                          value: row.original.severity,
                          name: row.original.severity,
                          label: row.original.severity,
                        }
                      : null
                  }
                  onChange={setEditingValue}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                  }}
                  placeholder="Search Severity"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(row.original._id, "severity", editingValue)
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "severity",
                });
                setEditingValue(
                  row.original.severity
                    ? {
                        value: row.original.severity,
                        name: row.original.severity,
                        label: row.original.severity,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.severity ? (
                row.original.severity
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Reaction",
        accessorKey: "reaction",
        size: 120,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "reaction" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 120, maxWidth: 180 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={false}
                  defaultOptions={false}
                  loadOptions={loadReactionOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.reaction
                      ? {
                          value: row.original.reaction,
                          name: row.original.reaction,
                          label: row.original.reaction,
                        }
                      : null
                  }
                  onChange={setEditingValue}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 120,
                      maxWidth: 180,
                    }),
                  }}
                  placeholder="Search Reaction"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(row.original._id, "reaction", editingValue)
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "reaction",
                });
                setEditingValue(
                  row.original.reaction
                    ? {
                        value: row.original.reaction,
                        name: row.original.reaction,
                        label: row.original.reaction,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.reaction ? (
                row.original.reaction
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Current Status",
        accessorKey: "currentstatus",
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "currentstatus" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 180, maxWidth: 220 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={true}
                  defaultOptions
                  loadOptions={loadConditionStatusOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.currentstatus
                      ? {
                          value: row.original.currentstatus,
                          name: row.original.currentstatus,
                          label: row.original.currentstatus,
                        }
                      : null
                  }
                  onChange={setEditingValue}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                  }}
                  placeholder="Select Status"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(
                    row.original._id,
                    "currentstatus",
                    editingValue?.value || editingValue?.label || ""
                  )
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "currentstatus",
                });
                setEditingValue(
                  row.original.currentstatus
                    ? {
                        value: row.original.currentstatus,
                        name: row.original.currentstatus,
                        label: row.original.currentstatus,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {typeof row.original.currentstatus === "string" &&
              row.original.currentstatus !== "" ? (
                row.original.currentstatus
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
    ],
    [editingCell, editingValue]
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    setSelectedAllergyIds(selectedIdsArray);
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess && moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT"))
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Allergies")}
          />
          <div className="container-fluid px-lg-5">
            <AllergiesHeader
              reloadList={getAllAllergies}
              selectedItemIds={selectedAllergyIds}
              updateBulkHandler={updateBulkAllergyHandler}
            />
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={allergyList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }}
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50,
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
          {/* popup and modals */}

          <SaveAllergyPopup
            selectedAllergyId={selectedAllergyId}
            setSelectedAllergyId={setSelectedAllergyId}
            afterPopupClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          {/* You can add SaveAllergyPopup and AssignUserModal here if needed */}
          <AssignUserModal
            moduleName="allergy"
            afterModalClose={afterActionClose}
            selectedIds={selectedAllergyIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedAllergyIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkAllergyHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedAllergyIds([]);
            }}
            moduleName={t("allergy")}
          />
          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default AllergiesListBody;
